/**
 * Utility functions for working with Servd's dynamic includes
 * Provides a centralized way to handle JavaScript re-initialization
 */

// Store callbacks that need to be re-run when dynamic content loads
const dynamicContentCallbacks = [];

/**
 * Register a callback to be executed when Servd loads dynamic content
 * @param {Function} callback - Function to execute
 * @param {string} name - Optional name for debugging
 */
export function registerDynamicCallback(callback, name = 'unnamed') {
  // Validate inputs
  if (typeof callback !== 'function') {
    throw new Error('registerDynamicCallback: callback must be a function');
  }
  if (typeof name !== 'string') {
    name = 'unnamed';
  }
  
  dynamicContentCallbacks.push({ callback, name });
}

/**
 * Execute all registered callbacks
 */
function executeDynamicCallbacks() {
  dynamicContentCallbacks.forEach(({ callback, name }) => {
    try {
      callback();
    } catch (error) {
      // Log error in development, silent in production
      if (process.env.NODE_ENV === 'development') {
        console.error(`Error in dynamic callback "${name}":`, error);
      }
    }
  });
}

/**
 * Manual trigger function for testing - call this in browser console
 * Usage: window.triggerServdEvent()
 */
export function triggerServdEvent() {
  executeDynamicCallbacks();
}

/**
 * Reflect the logged-in state onto the <body> so that user-specific UI
 * (e.g. article lock badges) can be toggled purely with CSS.
 *
 * The static page is cached as the guest version, so locked badges are
 * visible by default. The user-navigation partial is a Servd dynamic include
 * (never cached) that carries data-user-state; once it loads we flip the body
 * class, hiding the badges for members. This avoids a dynamic include per item.
 */
function applyUserState() {
  const nav = document.querySelector('.user-navigation[data-user-state]');
  if (!nav) return;
  const isMember = nav.getAttribute('data-user-state') === 'member';
  document.body.classList.toggle('is-member', isMember);
  document.body.classList.toggle('is-guest', !isMember);
}

/**
 * Simple user navigation initialization
 */
function initUserNavigation() {
  // Sync the guest/member body class from the (non-cached) user nav include
  applyUserState();

  // Find all user navigation toggles
  document.querySelectorAll('.user-navigation__toggle').forEach(toggle => {
    const menu = toggle.nextElementSibling;
    if (!menu || !menu.classList.contains('user-navigation__menu')) return;
    
    // Toggle menu
    toggle.addEventListener('click', function(e) {
      e.stopPropagation();
      const isExpanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !isExpanded);
      menu.hidden = isExpanded;
    });
  });

  // Find all logout buttons
  document.querySelectorAll('#logout-button').forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault();
      
      // Use Craft's default logout path instead of creating a dynamic form
      // This avoids CSRF token issues with Servd's static caching
      window.location.href = '/logout';
    });
  });

  // Close menus when clicking outside
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.user-navigation')) {
      document.querySelectorAll('.user-navigation__toggle').forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false');
        const menu = toggle.nextElementSibling;
        if (menu && menu.classList.contains('user-navigation__menu')) {
          menu.hidden = true;
        }
      });
    }
  });
}

/**
 * Initialize Servd event listeners
 * This should be called once when the page loads
 */
export function initServdListeners() {
  // Prevent multiple initializations
  if (window.servdListenersInitialized) {
    return;
  }
  window.servdListenersInitialized = true;
  
  // Initialize user navigation immediately
  initUserNavigation();
  
  // Listen for Servd dynamic include events
  window.addEventListener('servd.dynamicloaded', function(event) {
    // Re-initialize user navigation when dynamic content loads
    initUserNavigation();
    // Execute other callbacks
    executeDynamicCallbacks();
  });
  
  // Fallback: MutationObserver for other dynamic content
  const observer = new MutationObserver((mutations) => {
    let shouldTrigger = false;
    
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.querySelector && node.querySelector('img[data-src]')) {
              shouldTrigger = true;
            }
            if (node.matches && node.matches('img[data-src]')) {
              shouldTrigger = true;
            }
            if (node.querySelector && node.querySelector('.popup-gallery__image[data-src]')) {
              shouldTrigger = true;
            }
            if (node.matches && node.matches('.popup-gallery__image[data-src]')) {
              shouldTrigger = true;
            }
            if (node.querySelector && node.querySelector('.popup-gallery')) {
              shouldTrigger = true;
            }
            if (node.matches && node.matches('.popup-gallery')) {
              shouldTrigger = true;
            }
            // Check for user navigation
            if (node.querySelector && node.querySelector('.user-navigation__toggle')) {
              initUserNavigation();
            }
            if (node.matches && node.matches('.user-navigation__toggle')) {
              initUserNavigation();
            }
            // Sync guest/member body class as soon as the (non-cached) nav include
            // arrives — the guest variant has no toggle button, so match the wrapper.
            if (node.querySelector && node.querySelector('.user-navigation[data-user-state]')) {
              applyUserState();
            }
            if (node.matches && node.matches('.user-navigation[data-user-state]')) {
              applyUserState();
            }
          }
        });
      }
    });
    
    if (shouldTrigger) {
      executeDynamicCallbacks();
    }
  });
  
  // Start observing the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Make trigger function available globally for testing (only in development)
  if (process.env.NODE_ENV === 'development') {
    window.triggerServdEvent = triggerServdEvent;
  }
}

/**
 * Utility function to safely query for elements that might be in dynamic content
 * @param {string} selector - CSS selector
 * @param {Element} context - Optional context element (defaults to document)
 * @returns {NodeList} - Elements matching the selector
 */
export function queryDynamicElements(selector, context = document) {
  // Validate selector
  if (typeof selector !== 'string' || selector.trim() === '') {
    return document.querySelectorAll(''); // Return empty NodeList
  }
  
  // Validate context
  if (context && !(context instanceof Element) && context !== document) {
    context = document;
  }
  
  return context.querySelectorAll(selector);
}

/**
 * Utility function to check if an element is in dynamically loaded content
 * @param {Element} element - Element to check
 * @returns {boolean} - True if element is in dynamic content
 */
export function isDynamicElement(element) {
  // Validate input
  if (!(element instanceof Element)) {
    return false;
  }
  
  // This is a heuristic - you might need to adjust based on your setup
  return element.closest('[data-servd-dynamic]') !== null || 
         element.closest('.dynamic-content') !== null;
} 